import React, { useState, useEffect, useRef } from 'react';
import { TodoItem } from '../models/types';
import { tokens } from '@fluentui/react-components';
import { useTheme } from '../utils/theme';

interface TodoPanelProps {
  todos: TodoItem[];
}

const getStatusConfig = (dark: boolean) => ({
  pending: {
    label: 'pending',
    dot: '#CBD5E1',
    border: dark ? '#334155' : '#E2E8F0',
    text: '#94A3B8',
    pulse: false,
  },
  in_progress: {
    label: 'running',
    dot: '#F59E0B',
    border: '#FCD34D',
    text: dark ? '#FCD34D' : '#78350F',
    pulse: true,
  },
  completed: {
    label: 'done',
    dot: '#22C55E',
    border: dark ? '#166534' : '#86EFAC',
    text: dark ? '#4ADE80' : '#14532D',
    pulse: false,
  },
});

const TodoPanel: React.FC<TodoPanelProps> = ({ todos }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const prevTodosLengthRef = useRef(0);
  const { currentTheme } = useTheme();
  const dark = currentTheme.isDark;
  const STATUS_CONFIG = getStatusConfig(dark);

  // Auto-expand when new todos arrive
  useEffect(() => {
    if (todos.length > prevTodosLengthRef.current) {
      setIsExpanded(true);
    }
    prevTodosLengthRef.current = todos.length;
  }, [todos.length]);

  if (todos.length === 0) return null;

  const completedCount = todos.filter(t => t.status === 'completed').length;
  const inProgressCount = todos.filter(t => t.status === 'in_progress').length;
  const total = todos.length;
  const allDone = completedCount === total;

  return (
    <>
      <style>{`
        @keyframes todoPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        @keyframes todoFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes todoSlideDown {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 600px; }
        }
        .todo-item {
          animation: todoFadeIn 0.2s ease forwards;
        }
        .todo-dot-pulse {
          animation: todoPulse 1.4s ease-in-out infinite;
        }
        .todo-list-open {
          animation: todoSlideDown 0.2s ease forwards;
        }
        .todo-row:hover {
          background: ${dark ? '#1e293b' : '#F8FAFC'} !important;
        }
        .todo-panel-header:hover {
          background: ${dark ? '#2d3748' : '#F1F5F9'} !important;
        }
      `}</style>

      <div
        style={{
          borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
          backgroundColor: tokens.colorNeutralBackground1,
          flexShrink: 0,
        }}
      >
        {/* Header row */}
        <button
          className="todo-panel-header"
          onClick={() => setIsExpanded(prev => !prev)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '7px 16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'background 0.15s',
          }}
          aria-expanded={isExpanded}
          aria-label="Toggle agent plan"
        >
          {/* Clipboard icon */}
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="none"
            style={{ flexShrink: 0, color: tokens.colorNeutralForeground3 }}
          >
            <path
              d="M5 2H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1h-2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <rect
              x="5"
              y="1"
              width="6"
              height="3"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path d="M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>

          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: tokens.colorNeutralForeground2,
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            }}
          >
            Agent Plan
          </span>

          {/* Progress badge */}
          <span
            style={{
              fontSize: '10px',
              fontWeight: 600,
              padding: '1px 7px',
              borderRadius: '999px',
              backgroundColor: allDone
                ? (dark ? '#166534' : '#DCFCE7')
                : inProgressCount > 0
                  ? (dark ? '#451a03' : '#FEF3C7')
                  : (dark ? '#1e293b' : '#F1F5F9'),
              color: allDone
                ? (dark ? '#4ADE80' : '#15803D')
                : inProgressCount > 0
                  ? (dark ? '#FCD34D' : '#92400E')
                  : (dark ? '#94A3B8' : '#64748B'),
              border: `1px solid ${allDone ? (dark ? '#166534' : '#86EFAC') : inProgressCount > 0 ? '#FCD34D' : (dark ? '#334155' : '#E2E8F0')}`,
              fontFamily: 'ui-monospace, "Cascadia Code", monospace',
              transition: 'all 0.3s ease',
            }}
          >
            {completedCount}/{total}
          </span>

          {/* Spacer */}
          <span style={{ flex: 1 }} />

          {/* Chevron */}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              color: tokens.colorNeutralForeground3,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              flexShrink: 0,
            }}
          >
            <path
              d="M2 4l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Task list */}
        {isExpanded && (
          <div
            className="todo-list-open"
            style={{
              overflow: 'hidden',
              borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
            }}
          >
            {todos.map((todo, index) => {
              const cfg = STATUS_CONFIG[todo.status] ?? STATUS_CONFIG.pending;
              const isStrikethrough = todo.status === 'completed';

              return (
                <div
                  key={index}
                  className="todo-item todo-row"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '6px 16px 6px 18px',
                    borderLeft: `3px solid ${cfg.border}`,
                    transition: 'background 0.15s, border-color 0.3s',
                    animationDelay: `${index * 0.04}s`,
                    backgroundColor: tokens.colorNeutralBackground1,
                  }}
                >
                  {/* Status dot */}
                  <span
                    style={{
                      marginTop: '4px',
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      backgroundColor: cfg.dot,
                      flexShrink: 0,
                    }}
                    className={cfg.pulse ? 'todo-dot-pulse' : undefined}
                  />

                  {/* Task text */}
                  <span
                    style={{
                      fontSize: '12px',
                      fontFamily: 'ui-monospace, "Cascadia Code", "Fira Code", monospace',
                      color: isStrikethrough ? tokens.colorNeutralForeground3 : tokens.colorNeutralForeground1,
                      textDecoration: isStrikethrough ? 'line-through' : 'none',
                      lineHeight: '1.5',
                      flex: 1,
                      transition: 'color 0.3s, text-decoration 0.3s',
                    }}
                  >
                    {todo.content}
                  </span>

                  {/* Status label */}
                  <span
                    style={{
                      fontSize: '9px',
                      fontWeight: 700,
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      color: cfg.text,
                      fontFamily: 'ui-monospace, monospace',
                      paddingTop: '3px',
                      flexShrink: 0,
                      opacity: 0.8,
                    }}
                  >
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default TodoPanel;
